/* eslint-disable no-unused-vars */
// =============================================================================
// CampaignListScreen.js — FULL REBUILD
// JobEng — Business / Marketing
// Location: app/screens/business/marketing/CampaignListScreen.js
//
// Live DB: /api/data/campaigns (GET/PUT/POST/DELETE), cache fallback
// DB connection monitor: /health + table ping badge in header
// Features: search · status filters · 6 sort modes · bulk select mode
//           (long-press) with bulk activate/pause/delete · quick status toggle
//           · duplicate · action sheet with exports · stats strip · FAB
// Navigation targets preserved: CampaignDetails, EditCampaign, CreateCampaign,
//   CampaignAnalyticsDashboard, CampaignAIContentAssistantScreen,
//   ExportCampaignScreen (report/brochure/flyer), NotificationsCampaign
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Image,
  FlatList,
  Modal,
  Alert,
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';

const API_BASE = 'https://api.jobeng.ai';
const API_KEY =
  '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getH = async () => {
  const tok =
    (await AsyncStorage.getItem('auth_token').catch(() => null)) ||
    (await AsyncStorage.getItem('userToken').catch(() => null));
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

const getBid = async () =>
  parseInt(await AsyncStorage.getItem('company_id').catch(() => '0'), 10) || 0;

const C = {
  primary: '#0e4e96',
  primaryDark: '#093a72',
  primaryLight: '#1e7be2',
  accent: '#EC4899',
  bg: '#F4F6FB',
  card: '#FFFFFF',
  text: '#0F172A',
  sub: '#64748B',
  border: '#E2E8F0',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#7C3AED',
  teal: '#0891B2',
};

const STATUS_META = {
  draft: { label: 'Draft', color: '#94A3B8', icon: 'create-outline' },
  active: { label: 'Active', color: C.success, icon: 'play-circle-outline' },
  paused: { label: 'Paused', color: C.warning, icon: 'pause-circle-outline' },
  completed: { label: 'Completed', color: '#6366F1', icon: 'checkmark-circle-outline' },
};

const FILTERS = ['All', 'Active', 'Draft', 'Paused', 'Completed'];

const SORTS = [
  { key: 'newest', label: 'Newest first', icon: 'clock-outline' },
  { key: 'name', label: 'Name (A–Z)', icon: 'sort-alphabetical-ascending' },
  { key: 'budget', label: 'Budget (high → low)', icon: 'cash-multiple' },
  { key: 'spent', label: 'Spend (high → low)', icon: 'credit-card-outline' },
  { key: 'reach', label: 'Reach (high → low)', icon: 'eye-outline' },
  { key: 'ctr', label: 'CTR (high → low)', icon: 'chart-line' },
];

const CHANNEL_META = {
  email: { label: 'Email', icon: 'email-outline', color: C.teal },
  social: { label: 'Social', icon: 'share-variant-outline', color: C.accent },
  job_board: { label: 'Jobs', icon: 'briefcase-outline', color: C.primary },
  sms: { label: 'SMS', icon: 'message-text-outline', color: C.success },
  ppc: { label: 'Ads', icon: 'cursor-default-click-outline', color: C.warning },
  content: { label: 'Content', icon: 'post-outline', color: C.purple },
};

const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const money = (n) => `£${toNum(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const compact = (n) => {
  const v = toNum(n);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const parseChannels = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const j = JSON.parse(raw); if (Array.isArray(j)) return j; } catch {}
    return raw.split(',').map((x) => x.trim()).filter(Boolean);
  }
  return [];
};

const norm = (c) => {
  const impressions = toNum(c.impressions || c.reach || c.total_reach);
  const clicks = toNum(c.clicks);
  return {
    id: c.id,
    name: c.name || c.campaign_name || 'Untitled',
    description: c.description || '',
    objective: c.objective || '',
    status: (c.status || 'draft').toLowerCase(),
    channels: parseChannels(c.channels || c.channel || c.platforms),
    budget: toNum(c.budget),
    spent: toNum(c.spent || c.spend),
    impressions,
    clicks,
    conversions: toNum(c.conversions),
    ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    start_date: c.start_date ? String(c.start_date).slice(0, 10) : '',
    end_date: c.end_date ? String(c.end_date).slice(0, 10) : '',
    created_at: c.created_at || '',
    _raw: c,
  };
};

// =============================================================================
// MODULE-SCOPE COMPONENTS
// =============================================================================

const CompanyLogo = React.memo(({ uri, size = 28 }) => {
  if (uri && typeof uri === 'string' && (uri.startsWith('http') || uri.startsWith('data:'))) {
    return <Image source={{ uri }} style={{ width: size, height: size, borderRadius: 7 }} resizeMode="contain" onError={() => {}} />;
  }
  return (
    <View style={{ width: size, height: size, borderRadius: 7, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
      <MaterialCommunityIcons name="office-building" size={size * 0.6} color="#fff" />
    </View>
  );
});

const DbBadge = React.memo(({ status, onPress }) => {
  const cfg =
    status === 'online' ? { c: '#34D399', t: 'Live' }
    : status === 'offline' ? { c: '#F87171', t: 'Offline' }
    : { c: '#FBBF24', t: '…' };
  return (
    <TouchableOpacity style={s.dbBadge} onPress={onPress} activeOpacity={0.7}>
      <View style={[s.dbDot, { backgroundColor: cfg.c }]} />
      <Text style={s.dbText}>{cfg.t}</Text>
    </TouchableOpacity>
  );
});

const StatStrip = React.memo(({ stats }) => (
  <View style={s.statStrip}>
    {[
      { l: 'Total', v: String(stats.total), c: C.primary },
      { l: 'Active', v: String(stats.active), c: C.success },
      { l: 'Budget', v: money(stats.budget), c: C.primaryLight },
      { l: 'Spent', v: money(stats.spent), c: C.warning },
      { l: 'Reach', v: compact(stats.reach), c: C.purple },
    ].map((x) => (
      <View key={x.l} style={s.statCell}>
        <Text style={[s.statV, { color: x.c }]} numberOfLines={1}>{x.v}</Text>
        <Text style={s.statL}>{x.l}</Text>
      </View>
    ))}
  </View>
));

const FilterChip = React.memo(({ label, count, active, onPress }) => (
  <TouchableOpacity style={[s.chip, active && s.chipActive]} onPress={onPress} activeOpacity={0.7}>
    <Text style={[s.chipText, active && s.chipTextActive]}>
      {label}{count !== undefined ? ` (${count})` : ''}
    </Text>
  </TouchableOpacity>
));

const CampaignCard = React.memo(({ c, selectMode, selected, onPress, onLongPress, onToggleStatus, onMore }) => {
  const meta = STATUS_META[c.status] || STATUS_META.draft;
  const pct = c.budget > 0 ? Math.min(100, (c.spent / c.budget) * 100) : 0;
  const over = c.budget > 0 && c.spent > c.budget;
  return (
    <TouchableOpacity
      style={[s.card, selected && s.cardSelected]}
      onPress={() => onPress(c)}
      onLongPress={() => onLongPress(c)}
      activeOpacity={0.75}
      delayLongPress={300}
    >
      <View style={s.cardHead}>
        {selectMode && (
          <Ionicons
            name={selected ? 'checkbox' : 'square-outline'}
            size={20}
            color={selected ? C.primary : C.sub}
            style={{ marginRight: 10, marginTop: 2 }}
          />
        )}
        <View style={{ flex: 1, marginRight: 8 }}>
          <Text style={s.cardName} numberOfLines={1}>{c.name}</Text>
          {!!c.objective && <Text style={s.cardObjective} numberOfLines={1}>{c.objective}</Text>}
          {(c.start_date || c.end_date) && (
            <View style={s.dateRow}>
              <Ionicons name="calendar-outline" size={11} color={C.sub} />
              <Text style={s.dateText}>{c.start_date || '—'} → {c.end_date || '—'}</Text>
            </View>
          )}
        </View>
        <View style={{ alignItems: 'flex-end', gap: 6 }}>
          <View style={[s.statusPill, { backgroundColor: `${meta.color}18` }]}>
            <Ionicons name={meta.icon} size={11} color={meta.color} />
            <Text style={[s.statusPillText, { color: meta.color }]}>{meta.label}</Text>
          </View>
          {!selectMode && (
            <TouchableOpacity style={s.moreBtn} onPress={() => onMore(c)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="ellipsis-horizontal" size={17} color={C.sub} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {c.channels.length > 0 && (
        <View style={s.channelRow}>
          {c.channels.slice(0, 5).map((ch) => {
            const cm = CHANNEL_META[ch];
            return cm ? (
              <View key={ch} style={[s.channelBadge, { backgroundColor: `${cm.color}12` }]}>
                <MaterialCommunityIcons name={cm.icon} size={10} color={cm.color} />
                <Text style={[s.channelText, { color: cm.color }]}>{cm.label}</Text>
              </View>
            ) : null;
          })}
        </View>
      )}

      <View style={s.budgetRow}>
        <Text style={s.budgetText}>
          {money(c.spent)} <Text style={{ color: C.sub, fontWeight: '500' }}>/ {money(c.budget)}</Text>
        </Text>
        <Text style={[s.budgetPct, { color: over ? C.danger : C.sub }]}>{pct.toFixed(0)}%</Text>
      </View>
      <View style={s.track}>
        <View style={[s.fill, { width: `${pct}%`, backgroundColor: over ? C.danger : C.primaryLight }]} />
      </View>

      <View style={s.metricsRow}>
        <View style={s.metric}><Text style={s.metricV}>{compact(c.impressions)}</Text><Text style={s.metricL}>Reach</Text></View>
        <View style={s.metric}><Text style={s.metricV}>{compact(c.clicks)}</Text><Text style={s.metricL}>Clicks</Text></View>
        <View style={s.metric}><Text style={s.metricV}>{c.ctr.toFixed(1)}%</Text><Text style={s.metricL}>CTR</Text></View>
        <View style={s.metric}><Text style={s.metricV}>{compact(c.conversions)}</Text><Text style={s.metricL}>Conv.</Text></View>
        {!selectMode && c.status !== 'completed' && (
          <TouchableOpacity style={s.quickToggle} onPress={() => onToggleStatus(c)}>
            <Ionicons name={c.status === 'active' ? 'pause' : 'play'} size={15} color="#fff" />
          </TouchableOpacity>
        )}
      </View>
    </TouchableOpacity>
  );
});

const SheetOption = React.memo(({ icon, label, color, onPress, last }) => (
  <TouchableOpacity style={[s.sheetOption, last && { borderBottomWidth: 0 }]} onPress={onPress} activeOpacity={0.7}>
    <MaterialCommunityIcons name={icon} size={19} color={color || C.text} />
    <Text style={[s.sheetOptionText, color ? { color } : null]}>{label}</Text>
  </TouchableOpacity>
));

// =============================================================================
// MAIN SCREEN
// =============================================================================

const CampaignListScreen = ({ navigation }) => {
  const [companyLogo, setCompanyLogo] = useState(null);
  const [businessId, setBusinessId] = useState(0);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [sortBy, setSortBy] = useState('newest');
  const [showSort, setShowSort] = useState(false);

  const [actionCampaign, setActionCampaign] = useState(null);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [dbStatus, setDbStatus] = useState('checking');
  const [dbDetail, setDbDetail] = useState('');

  // ---------------------------------------------------------------------------
  // DB CHECK
  // ---------------------------------------------------------------------------

  const checkDb = useCallback(async () => {
    setDbStatus('checking');
    try {
      const h = await getH();
      const t0 = Date.now();
      const health = await fetch(`${API_BASE}/health`, { headers: h }).then((r) => r.ok).catch(() => false);
      const table = await fetch(`${API_BASE}/api/data/campaigns?limit=1`, { headers: h }).then((r) => r.ok).catch(() => false);
      const ms = Date.now() - t0;
      if (health && table) { setDbStatus('online'); setDbDetail(`API + campaigns table reachable · ${ms}ms`); }
      else if (health) { setDbStatus('offline'); setDbDetail('API up, campaigns table unreachable — check /api/data/campaigns'); }
      else { setDbStatus('offline'); setDbDetail('API not responding — check PM2 / nginx'); }
    } catch (e) { setDbStatus('offline'); setDbDetail(e?.message || 'Network error'); }
  }, []);

  const showDbDetail = useCallback(() => {
    Alert.alert(
      dbStatus === 'online' ? '✅ Connected' : '⚠️ Connection issue',
      dbDetail || 'Checking…',
      [{ text: 'Re-check', onPress: checkDb }, { text: 'OK' }]
    );
  }, [dbStatus, dbDetail, checkDb]);

  // ---------------------------------------------------------------------------
  // LOAD
  // ---------------------------------------------------------------------------

  const load = useCallback(async (spinner = true) => {
    if (spinner) setLoading(true);
    const bid = await getBid();
    setBusinessId(bid);
    if (!bid) { setCampaigns([]); setLoading(false); setRefreshing(false); return; }
    try {
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/campaigns?company_id=${bid}&limit=200`, { headers: h })
        .then((r) => r.json()).catch(() => null);
      const rows = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : null;
      if (rows) {
        setCampaigns(rows.map(norm));
        AsyncStorage.setItem('campaigns_cache', JSON.stringify(rows.slice(0, 50))).catch(() => {});
      } else { throw new Error('bad response'); }
    } catch {
      const cached = await AsyncStorage.getItem('campaigns_cache').catch(() => null);
      if (cached) { try { setCampaigns(JSON.parse(cached).map(norm)); } catch {} }
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    AsyncStorage.getItem('company_profile').then((raw) => {
      if (!raw) return;
      try {
        const p = JSON.parse(raw);
        const logo = p.logo || p.logo_url || p.company_logo;
        if (logo && typeof logo === 'string') {
          if (logo.startsWith('http') || logo.startsWith('data:')) setCompanyLogo(logo);
          else if (logo.startsWith('/')) setCompanyLogo(API_BASE + logo);
        }
      } catch {}
    }).catch(() => {});
    checkDb();
    load();
    const unsub = navigation?.addListener?.('focus', () => load(false));
    return unsub;
  }, [checkDb, load, navigation]);

  const refresh = useCallback(() => { setRefreshing(true); checkDb(); load(false); }, [checkDb, load]);

  // ---------------------------------------------------------------------------
  // ACTIONS
  // ---------------------------------------------------------------------------

  const toggleStatus = useCallback(async (c) => {
    const next = c.status === 'active' ? 'paused' : 'active';
    setCampaigns((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: next } : x)));
    try {
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/campaigns/${c.id}`, {
        method: 'PUT', headers: h, body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      setCampaigns((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: c.status } : x)));
      Alert.alert('Update failed', e?.message || 'Could not update status.');
    }
  }, []);

  const deleteCampaign = useCallback((c) => {
    Alert.alert('Delete campaign', `Delete "${c.name}"? This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive',
        onPress: async () => {
          try {
            const h = await getH();
            await fetch(`${API_BASE}/api/data/campaigns/${c.id}`, { method: 'DELETE', headers: h }).catch(() => {});
            setCampaigns((prev) => prev.filter((x) => x.id !== c.id));
            const cache = JSON.parse((await AsyncStorage.getItem('campaigns_cache').catch(() => '[]')) || '[]').filter((x) => x.id !== c.id);
            AsyncStorage.setItem('campaigns_cache', JSON.stringify(cache)).catch(() => {});
          } catch { Alert.alert('Delete failed', 'Could not delete campaign.'); }
        },
      },
    ]);
  }, []);

  const duplicateCampaign = useCallback(async (c) => {
    try {
      const h = await getH();
      const bid = await getBid();
      const r = c._raw || {};
      const res = await fetch(`${API_BASE}/api/data/campaigns`, {
        method: 'POST', headers: h,
        body: JSON.stringify({
          company_id: bid,
          name: `${c.name} (Copy)`,
          description: r.description || '',
          objective: r.objective || '',
          channels: typeof r.channels === 'string' ? r.channels : JSON.stringify(c.channels),
          target_audience: r.target_audience || '',
          budget: c.budget, status: 'draft',
          spent: 0, impressions: 0, clicks: 0, conversions: 0,
          start_date: r.start_date || null, end_date: r.end_date || null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      load(false);
    } catch (e) { Alert.alert('Duplicate failed', e?.message || 'Could not duplicate.'); }
  }, [load]);

  // ---------------------------------------------------------------------------
  // BULK SELECT
  // ---------------------------------------------------------------------------

  const enterSelect = useCallback((c) => {
    setSelectMode(true);
    setSelectedIds([c.id]);
  }, []);

  const toggleSelect = useCallback((c) => {
    setSelectedIds((prev) => (prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id]));
  }, []);

  const exitSelect = useCallback(() => { setSelectMode(false); setSelectedIds([]); }, []);

  const bulkStatus = useCallback(async (status) => {
    const ids = [...selectedIds];
    setCampaigns((prev) => prev.map((x) => (ids.includes(x.id) ? { ...x, status } : x)));
    exitSelect();
    const h = await getH();
    await Promise.all(
      ids.map((id) =>
        fetch(`${API_BASE}/api/data/campaigns/${id}`, {
          method: 'PUT', headers: h, body: JSON.stringify({ status }),
        }).catch(() => null)
      )
    );
    load(false);
  }, [selectedIds, exitSelect, load]);

  const bulkDelete = useCallback(() => {
    const ids = [...selectedIds];
    Alert.alert('Delete campaigns', `Delete ${ids.length} campaign${ids.length > 1 ? 's' : ''}? This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete all', style: 'destructive',
        onPress: async () => {
          setCampaigns((prev) => prev.filter((x) => !ids.includes(x.id)));
          exitSelect();
          const h = await getH();
          await Promise.all(ids.map((id) => fetch(`${API_BASE}/api/data/campaigns/${id}`, { method: 'DELETE', headers: h }).catch(() => null)));
          load(false);
        },
      },
    ]);
  }, [selectedIds, exitSelect, load]);

  // ---------------------------------------------------------------------------
  // DERIVED
  // ---------------------------------------------------------------------------

  const stats = useMemo(() => ({
    total: campaigns.length,
    active: campaigns.filter((c) => c.status === 'active').length,
    budget: campaigns.reduce((x, c) => x + c.budget, 0),
    spent: campaigns.reduce((x, c) => x + c.spent, 0),
    reach: campaigns.reduce((x, c) => x + c.impressions, 0),
  }), [campaigns]);

  const counts = useMemo(() => {
    const m = { All: campaigns.length };
    FILTERS.slice(1).forEach((f) => { m[f] = campaigns.filter((c) => c.status === f.toLowerCase()).length; });
    return m;
  }, [campaigns]);

  const filtered = useMemo(() => {
    let list = [...campaigns];
    if (activeFilter !== 'All') list = list.filter((c) => c.status === activeFilter.toLowerCase());
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.objective.toLowerCase().includes(q) ||
          c.channels.some((p) => p.toLowerCase().includes(q))
      );
    }
    switch (sortBy) {
      case 'name': list.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'budget': list.sort((a, b) => b.budget - a.budget); break;
      case 'spent': list.sort((a, b) => b.spent - a.spent); break;
      case 'reach': list.sort((a, b) => b.impressions - a.impressions); break;
      case 'ctr': list.sort((a, b) => b.ctr - a.ctr); break;
      default: list.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
    }
    return list;
  }, [campaigns, activeFilter, search, sortBy]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const onCardPress = useCallback((c) => {
    if (selectMode) toggleSelect(c);
    else navigation.navigate('CampaignDetails', { campaign: c._raw || c, campaignId: c.id });
  }, [selectMode, toggleSelect, navigation]);

  const renderItem = useCallback(({ item }) => (
    <CampaignCard
      c={item}
      selectMode={selectMode}
      selected={selectedIds.includes(item.id)}
      onPress={onCardPress}
      onLongPress={selectMode ? toggleSelect : enterSelect}
      onToggleStatus={toggleStatus}
      onMore={setActionCampaign}
    />
  ), [selectMode, selectedIds, onCardPress, toggleSelect, enterSelect, toggleStatus]);

  return (
    <SafeAreaView style={s.safe} edges={['top']}>
      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity style={s.headerBtn} onPress={() => (selectMode ? exitSelect() : navigation.goBack())}>
          <Ionicons name={selectMode ? 'close' : 'chevron-back'} size={24} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={s.headerTitle}>{selectMode ? `${selectedIds.length} selected` : 'Campaigns'}</Text>
          {!selectMode && <Text style={s.headerSub}>{campaigns.length} total · long-press to multi-select</Text>}
        </View>
        {!selectMode && (
          <>
            <DbBadge status={dbStatus} onPress={showDbDetail} />
            <TouchableOpacity style={s.headerBtn} onPress={() => navigation.navigate('NotificationsCampaign')}>
              <Ionicons name="notifications-outline" size={21} color="#fff" />
            </TouchableOpacity>
            <CompanyLogo uri={companyLogo} size={28} />
          </>
        )}
      </View>

      {/* Bulk action bar */}
      {selectMode && (
        <View style={s.bulkBar}>
          <TouchableOpacity style={s.bulkBtn} onPress={() => setSelectedIds(filtered.map((c) => c.id))}>
            <Ionicons name="checkbox-outline" size={15} color="#fff" />
            <Text style={s.bulkText}>All</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.bulkBtn, { backgroundColor: C.success }]} onPress={() => bulkStatus('active')} disabled={!selectedIds.length}>
            <Ionicons name="play" size={14} color="#fff" />
            <Text style={s.bulkText}>Activate</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.bulkBtn, { backgroundColor: C.warning }]} onPress={() => bulkStatus('paused')} disabled={!selectedIds.length}>
            <Ionicons name="pause" size={14} color="#fff" />
            <Text style={s.bulkText}>Pause</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.bulkBtn, { backgroundColor: C.danger }]} onPress={bulkDelete} disabled={!selectedIds.length}>
            <Ionicons name="trash-outline" size={14} color="#fff" />
            <Text style={s.bulkText}>Delete</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Search + sort */}
      {!selectMode && (
        <View style={s.toolRow}>
          <View style={[s.searchWrap, { flex: 1 }]}>
            <Ionicons name="search" size={15} color={C.sub} />
            <TextInput style={s.searchInput} value={search} onChangeText={setSearch}
              placeholder="Search name, objective, channel…" placeholderTextColor="#9CA3AF" />
            {search.length > 0 && (
              <TouchableOpacity onPress={() => setSearch('')}>
                <Ionicons name="close-circle" size={15} color={C.sub} />
              </TouchableOpacity>
            )}
          </View>
          <TouchableOpacity style={s.sortBtn} onPress={() => setShowSort(true)}>
            <MaterialCommunityIcons name="sort" size={19} color={C.primary} />
          </TouchableOpacity>
        </View>
      )}

      <FlatList
        data={filtered}
        keyExtractor={(item) => String(item.id)}
        renderItem={renderItem}
        style={{ backgroundColor: C.bg }}
        contentContainerStyle={{ paddingHorizontal: 14, paddingBottom: 90 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={C.primary} />}
        ListHeaderComponent={
          <View>
            <StatStrip stats={stats} />
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chipRow}>
              {FILTERS.map((f) => (
                <FilterChip key={f} label={f} count={counts[f]} active={activeFilter === f} onPress={() => setActiveFilter(f)} />
              ))}
            </ScrollView>
          </View>
        }
        ListEmptyComponent={
          loading ? (
            <ActivityIndicator size="large" color={C.primary} style={{ marginTop: 50 }} />
          ) : (
            <View style={s.empty}>
              <MaterialCommunityIcons name="bullhorn-outline" size={48} color="#CBD5E1" />
              <Text style={s.emptyTitle}>
                {businessId === 0 ? 'No company context' : 'No campaigns found'}
              </Text>
              <Text style={s.emptySub}>
                {businessId === 0
                  ? 'Sign in to your business account.'
                  : search || activeFilter !== 'All'
                  ? 'Try changing your filters.'
                  : 'Create your first campaign.'}
              </Text>
            </View>
          )
        }
      />

      {/* FAB */}
      {!selectMode && (
        <TouchableOpacity style={s.fab} onPress={() => navigation.navigate('CreateCampaign')} activeOpacity={0.85}>
          <Ionicons name="add" size={26} color="#fff" />
        </TouchableOpacity>
      )}

      {/* Sort sheet */}
      <Modal visible={showSort} transparent animationType="slide" onRequestClose={() => setShowSort(false)}>
        <TouchableOpacity style={s.sheetOverlay} activeOpacity={1} onPress={() => setShowSort(false)}>
          <View style={s.sheet}>
            <Text style={s.sheetTitle}>Sort campaigns</Text>
            {SORTS.map((op, i) => (
              <TouchableOpacity key={op.key} style={[s.sortRow, i === SORTS.length - 1 && { borderBottomWidth: 0 }]}
                onPress={() => { setSortBy(op.key); setShowSort(false); }}>
                <MaterialCommunityIcons name={op.icon} size={18} color={sortBy === op.key ? C.primary : C.sub} />
                <Text style={[s.sortText, sortBy === op.key && { color: C.primary, fontWeight: '800' }]}>{op.label}</Text>
                {sortBy === op.key && <Ionicons name="checkmark" size={18} color={C.primary} />}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Action sheet */}
      <Modal visible={!!actionCampaign} transparent animationType="slide" onRequestClose={() => setActionCampaign(null)}>
        <TouchableOpacity style={s.sheetOverlay} activeOpacity={1} onPress={() => setActionCampaign(null)}>
          <View style={s.sheet}>
            <Text style={s.sheetTitle} numberOfLines={1}>{actionCampaign?.name}</Text>
            <SheetOption icon="information-outline" label="View Details"
              onPress={() => { navigation.navigate('CampaignDetails', { campaign: actionCampaign._raw, campaignId: actionCampaign.id }); setActionCampaign(null); }} />
            <SheetOption icon="pencil-outline" label="Edit Campaign"
              onPress={() => { navigation.navigate('EditCampaign', { campaign: actionCampaign._raw }); setActionCampaign(null); }} />
            <SheetOption icon="chart-bar" label="Analytics"
              onPress={() => { navigation.navigate('CampaignAnalyticsDashboard', { campaign: actionCampaign._raw }); setActionCampaign(null); }} />
            <SheetOption icon="robot-outline" label="AI Content"
              onPress={() => { navigation.navigate('CampaignAIContentAssistantScreen', { campaign: actionCampaign._raw }); setActionCampaign(null); }} />
            <SheetOption icon="content-copy" label="Duplicate"
              onPress={() => { duplicateCampaign(actionCampaign); setActionCampaign(null); }} />
            <SheetOption icon="file-pdf-box" label="Campaign Report (PDF)" color={C.danger}
              onPress={() => { navigation.navigate('ExportCampaignScreen', { type: 'report', campaign: actionCampaign._raw || actionCampaign, campaignId: actionCampaign.id }); setActionCampaign(null); }} />
            <SheetOption icon="book-open-variant" label="Marketing Brochure" color={C.purple}
              onPress={() => { navigation.navigate('ExportCampaignScreen', { type: 'brochure', campaign: actionCampaign._raw || actionCampaign, campaignId: actionCampaign.id }); setActionCampaign(null); }} />
            <SheetOption icon="newspaper-variant-outline" label="Print Flyer" color={C.teal}
              onPress={() => { navigation.navigate('ExportCampaignScreen', { type: 'flyer', campaign: actionCampaign._raw || actionCampaign, campaignId: actionCampaign.id }); setActionCampaign(null); }} />
            <SheetOption icon="trash-can-outline" label="Delete Campaign" color={C.danger} last
              onPress={() => { const c = actionCampaign; setActionCampaign(null); deleteCampaign(c); }} />
          </View>
        </TouchableOpacity>
      </Modal>
    </SafeAreaView>
  );
};

// =============================================================================
// STYLES
// =============================================================================

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.primary },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.primary, paddingHorizontal: 10, paddingVertical: 10, gap: 8 },
  headerBtn: { padding: 5 },
  headerTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.6)', fontSize: 10.5, marginTop: 1 },

  dbBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.14)', borderRadius: 12, paddingHorizontal: 9, paddingVertical: 5 },
  dbDot: { width: 7, height: 7, borderRadius: 4 },
  dbText: { color: '#fff', fontSize: 10.5, fontWeight: '700' },

  bulkBar: { flexDirection: 'row', backgroundColor: C.primaryDark, paddingHorizontal: 10, paddingVertical: 8, gap: 8 },
  bulkBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 10, paddingVertical: 8 },
  bulkText: { color: '#fff', fontSize: 11.5, fontWeight: '800' },

  toolRow: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: C.primary, paddingHorizontal: 12, paddingBottom: 10 },
  searchWrap: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 11, paddingHorizontal: 10 },
  searchInput: { flex: 1, paddingVertical: Platform.OS === 'ios' ? 9 : 7, paddingHorizontal: 8, fontSize: 13.5, color: C.text },
  sortBtn: { width: 38, height: 38, borderRadius: 11, backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center' },

  statStrip: { flexDirection: 'row', backgroundColor: C.card, borderRadius: 14, marginTop: 12, paddingVertical: 11, borderWidth: 1, borderColor: C.border },
  statCell: { flex: 1, alignItems: 'center' },
  statV: { fontSize: 12.5, fontWeight: '800' },
  statL: { fontSize: 9.5, color: C.sub, marginTop: 2 },

  chipRow: { gap: 8, paddingVertical: 10 },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 15, backgroundColor: C.card, borderWidth: 1, borderColor: C.border },
  chipActive: { backgroundColor: C.primary, borderColor: C.primary },
  chipText: { fontSize: 12, fontWeight: '600', color: C.sub },
  chipTextActive: { color: '#fff' },

  card: { backgroundColor: C.card, borderRadius: 14, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: C.border },
  cardSelected: { borderColor: C.primary, borderWidth: 2 },
  cardHead: { flexDirection: 'row', alignItems: 'flex-start' },
  cardName: { fontSize: 14.5, fontWeight: '700', color: C.text },
  cardObjective: { fontSize: 11.5, color: C.primaryLight, fontWeight: '600', marginTop: 2 },
  dateRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 4 },
  dateText: { fontSize: 10.5, color: C.sub },
  statusPill: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10 },
  statusPillText: { fontSize: 10, fontWeight: '800' },
  moreBtn: { padding: 2 },

  channelRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginTop: 8 },
  channelBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, borderRadius: 7, paddingHorizontal: 6, paddingVertical: 3 },
  channelText: { fontSize: 9.5, fontWeight: '700' },

  budgetRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 },
  budgetText: { fontSize: 12, fontWeight: '800', color: C.text },
  budgetPct: { fontSize: 11.5, fontWeight: '700' },
  track: { height: 5, backgroundColor: '#E8EDF6', borderRadius: 3, marginTop: 5, overflow: 'hidden' },
  fill: { height: 5, borderRadius: 3 },

  metricsRow: { flexDirection: 'row', alignItems: 'center', marginTop: 10, backgroundColor: '#F8FAFD', borderRadius: 10, paddingVertical: 8, paddingRight: 6 },
  metric: { flex: 1, alignItems: 'center' },
  metricV: { fontSize: 12.5, fontWeight: '800', color: C.text },
  metricL: { fontSize: 9.5, color: C.sub, marginTop: 1 },
  quickToggle: { width: 30, height: 30, borderRadius: 15, backgroundColor: C.primary, alignItems: 'center', justifyContent: 'center' },

  empty: { alignItems: 'center', paddingTop: 50, paddingHorizontal: 30 },
  emptyTitle: { fontSize: 15.5, fontWeight: '700', color: C.text, marginTop: 12 },
  emptySub: { fontSize: 12.5, color: C.sub, textAlign: 'center', marginTop: 5, lineHeight: 18 },

  fab: { position: 'absolute', right: 18, bottom: 26, width: 54, height: 54, borderRadius: 27, backgroundColor: C.primary, alignItems: 'center', justifyContent: 'center', elevation: 5, shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 6, shadowOffset: { width: 0, height: 3 } },

  sheetOverlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.45)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: C.card, borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: 16, paddingBottom: 28 },
  sheetTitle: { fontSize: 14.5, fontWeight: '800', color: C.text, marginBottom: 6 },
  sheetOption: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  sheetOptionText: { fontSize: 14, fontWeight: '600', color: C.text },
  sortRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  sortText: { flex: 1, fontSize: 14, fontWeight: '600', color: C.text },
});

export default CampaignListScreen;
